import { Link, useNavigate, useParams } from "react-router-dom";
import Container from "../../../components/Container";
import { Helmet } from "react-helmet";
import { useEffect, useState } from "react";
import { INoteRecord, Status } from "../../../lib/types";
import pb from "../../../lib/pocketbase";
import BackButton from "../../../components/form/BackButton";
import { Input } from "../../../components/ui/input";
import MarkdownEditor from "../../../components/form/MarkdownEditor";
import Button from "../../../components/form/button";
import Confirmation from "../../../components/form/Confirmation";
import { Switch } from "../../../components/ui/switch";
import Loading from "../../../components/Loading";
import { config } from "../../../config";
import { createSlug } from "../../../lib/utils";

export default function EditNote() {
    const noteId = useParams().note;
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [note, setNote] = useState<INoteRecord>();
    const [name, setName] = useState("");
    const [isPublic, setIsPublic] = useState(false);
    const [content, setContent] = useState("");
    const [status, setStatus] = useState<Status>("none");

    async function load() {
        setIsLoading(true);
        if (!noteId) {
            navigate("/404");
            return;
        }
        const n: INoteRecord = await pb.collection("note").getOne(noteId, {
            expand: "folder"
        });
        setName(n.name);
        setIsPublic(n.public);
        setContent(n.content);
        setNote(n);
        setIsLoading(false);
    }

    async function save() {
        if (!noteId) return;
        setStatus("loading");
        const data = {
            name: name,
            content: content,
            public: isPublic
        };

        pb.collection("note")
            .update(noteId, data)
            .then((x) => {
                setTimeout(() => {
                    setStatus("success");
                    navigate(`/studio/notes/note/${x.id}`);
                }, 500);
            })
            .catch((err: Error) => {
                setStatus("error");
            })
            .finally(() => {
                setTimeout(() => {
                    setStatus("none");
                }, 3000);
            });
    }
    function deleteNote() {
        if (!noteId) return;
        pb.collection("note").delete(noteId);
        navigate(`/studio/notes/${note?.folder}`);
    }

    useEffect(() => {
        load();
    }, []);

    const slug = createSlug(name);

    return (
        <>
            <Helmet>
                <title>ReinVB Studio — Note</title>
            </Helmet>
            {isLoading && (
                <div className="mt-8 flex justify-center">
                    <Loading />
                </div>
            )}
            {!isLoading && note && (
                <Container className="mt-8 lg:mt-12">
                    <div className="flex items-center gap-4">
                        <BackButton size={"box"} />
                        <div className="flex-grow">
                            <Input value={name} onChange={(e) => setName(e.target.value)} />
                        </div>
                    </div>
                    <p className="mt-4 flex items-center gap-2">
                        Public: <Switch checked={isPublic} onCheckedChange={setIsPublic} />
                    </p>
                    {isPublic && (
                        <div className="mt-2 flex items-center gap-2 rounded bg-slate-100 px-4 py-2 text-slate-500 dark:bg-slate-800 dark:text-slate-500">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                            </svg>
                            <Link target="_blank" to={`${config.base}/note/${slug}/${note.id}`}>{`${config.base}/note/${slug}/${note.id}`}</Link>
                        </div>
                    )}

                    <div className="mt-8">
                        <MarkdownEditor className="scrollbar h-[calc(100dvh-24rem)] overflow-y-scroll" value={content} onChange={(text) => setContent(text)} placeholder="Enter your note content in Markdown..." />
                    </div>
                    <div className="mt-8 flex items-center gap-2">
                        <Button status={status} onClick={() => save()} variant="dark">
                            {status === "loading" && "Saving"}
                            {status === "success" && "Saved"}
                            {status === "error" && "Error"}
                            {status === "none" && "Save"}
                        </Button>
                        <Confirmation onConfirm={deleteNote}>
                            <Button>Delete</Button>
                        </Confirmation>
                    </div>
                </Container>
            )}
        </>
    );
}
